import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import { useNotify } from 'react-admin';

interface PasswordResetDialogProps {
  open: boolean;
  username: string;
  tempPassword: string;
  onClose: () => void;
}

/**
 * PasswordResetDialog - Shows generated temp password with copy button
 */
export const PasswordResetDialog = ({
  open,
  username,
  tempPassword,
  onClose,
}: PasswordResetDialogProps) => {
  const notify = useNotify();

  const handleCopy = () => {
    navigator.clipboard.writeText(tempPassword);
    notify('Password copied to clipboard', { type: 'success' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Password Reset Successful</DialogTitle>
      <DialogContent>
        <Alert severity="success" sx={{ mb: 2 }}>
          Temporary password has been generated for <strong>{username}</strong>
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please share this temporary password with the user. They should change it
          immediately after logging in.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            value={tempPassword}
            fullWidth
            InputProps={{
              readOnly: true,
              sx: {
                fontFamily: 'monospace',
                fontSize: '1.1rem',
                fontWeight: 'bold',
              },
            }}
          />
          <IconButton onClick={handleCopy} color="primary">
            <CopyIcon />
          </IconButton>
        </Box>

        <Alert severity="warning" sx={{ mt: 2 }}>
          This password will only be shown once. Make sure to save it before closing
          this dialog.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
